// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CertificationBadges } from "@eisinc/App/Components/CertificationBadges/CertificationBadges";
import Notify from "@eisinc/App/Components/Notify";
import QtyField from "@eisinc/App/Components/Qty/QtyField";
import cartLineSharedStyles from "@eisinc/App/SharedStyles/CartLineSharedStyles";
import { xPartNumberStyle } from "@eisinc/App/SharedStyles/ProductPartNumberStyle";
import { createVerticalSmallHeadingAndTextStyles } from "@eisinc/App/SharedStyles/smallHeadingAndTextStyles";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@eisinc/MobiusOverrides/DatePicker";
import ProductBrand from "@eisinc/Overrides/Components/ProductBrand";
import { CART_BREAKPOINT } from "@eisinc/XDL/constants";
import Delete from "@eisinc/XDL/Icons/Delete";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, createLinkIconStyles, parseBoolean, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductImage from "@insite/content-library/Components/ProductImage";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useMemo } from "react";
import { css } from "styled-components";

export interface ScheduleShipment {
    id: string;
    requestedDeliveryDate: string | null; // null = Ships ASAP
    requestedDeliveryDateDisplay?: string;
    qty: number;
}

export interface LineScheduleState {
    cartLineId: string;
    totalQty: number;
    items: ScheduleShipment[];
    isSelected: boolean;
    unitOfMeasure: string;
    productId: string;
}

interface ScheduleOrderLineItemProps {
    cart: Cart;
    cartLine: CartLineModel;
    isSchedulable: boolean;
    isSelected: boolean;
    onCheckboxChange?: (lineId: string, isChecked: boolean) => void;
    scheduleState?: LineScheduleState;
    minScheduleDate: Date;
    onAddShipmentDate?: (lineId: string) => void;
    onRemoveShipmentDate?: (lineId: string, shipmentId: string) => void;
    onShipmentQtyChange?: (lineId: string, shipmentId: string, qty: number) => void;
    onShipmentDateChange?: (lineId: string, shipmentId: string, requestedDeliveryDate: string | null) => void;
    /** Warning message to display above the line item (e.g., duplicate date) */
    warningMessage?: string;
}

export interface ScheduleOrderLineItemStyles {
    wrapper?: InjectableCss;
    warningBanner?: InjectableCss;
    container?: InjectableCss;
    checkboxColumn?: InjectableCss;
    imageColumn?: InjectableCss;
    descriptionColumn?: InjectableCss;
    qtyText?: TypographyPresentationProps;
    asapText?: TypographyPresentationProps;
    scheduleQtyColumn?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    partNumbers?: InjectableCss;
    partNumberRow?: InjectableCss;
    scheduleRow?: InjectableCss;
    scheduleRowAsap?: InjectableCss;
    scheduleRowAdded?: InjectableCss;
    addShipmentLink?: LinkPresentationProps;
    qtyStepperWrapper?: InjectableCss;
    qtyDateWrapper?: InjectableCss;
    scheduleQtyAndDateLabel?: TypographyPresentationProps;
    dateRowWrapper?: InjectableCss;
    deleteButton?: InjectableCss;
    datePicker?: DatePickerPresentationProps;
    clickable?: ClickablePresentationProps;
    totalSmallHeadingAndText?: SmallHeadingAndTextStyles;
    qtySmallHeadingAndText?: SmallHeadingAndTextStyles;
}

export const scheduleOrderLineItemStyles: ScheduleOrderLineItemStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            border-bottom: 1px solid ${color("common.ui3")};
            &:last-child {
                border-bottom: 0;
            }
        `,
    },
    warningBanner: {
        css: css`
            max-width: 100%;
            margin: 12px 0 0;
        `,
    },
    container: {
        css: css`
            width: 100%;
            padding: 12px 0 12px 8px;
            display: grid;
            gap: 8px 16px;
            /* Checkbox | Image | Description | Total | QTY In Cart | Schedule QTY | Schedule Date */
            grid-template-columns: 30px 92px minmax(280px, 504px) 190px 124px minmax(354px, 1fr);
            ${xScreen("lte", CART_BREAKPOINT)} {
                grid-template-columns: 30px 92px minmax(180px, 380px) 150px 100px minmax(354px, 1fr);
            }

            ${xScreen("lte", "md")} {
                grid-template-columns: 72px 16px 1fr;
                padding: 12px 0;
                gap: 12px;
            }

            /* When no checkbox (unavailable items) */
            &.no-checkbox {
                grid-template-columns: 92px minmax(280px, 504px) 190px 124px 124px minmax(218px, 1fr);

                ${xScreen("lte", CART_BREAKPOINT)} {
                    grid-template-columns: 92px minmax(180px, 380px) 150px 100px 100px minmax(218px, 1fr);
                }

                ${xScreen("lte", "md")} {
                    grid-template-columns: 72px 16px 1fr;
                }
            }
        `,
    },
    qtyDateWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 124px 1fr;
            gap: 12px;
            ${xScreen("lte", "md")} {
                grid-template-columns: 100px 1fr;
                width: 100%;
            }
            padding-bottom: 12px;
            border-bottom: 1px solid ${color("common.ui3")};
            &.hide-border {
                border: 0;
            }
        `,
    },
    scheduleQtyAndDateLabel: {
        ...fontConfig.singleLinexSmall,
        weight: fontConfig.weight.medium,
    },
    checkboxColumn: {
        css: css`
            display: flex;
            align-items: flex-start;
            padding-top: 4px;
            ${xScreen("lte", "md")} {
                grid-column: 1/-1;
            }
        `,
    },
    imageColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 6px;

            figure {
                margin: 0;
            }

            img {
                max-width: 92px;
                max-height: 92px;
                ${xScreen("lte", "md")} {
                    max-width: 72px;
                    max-height: 72px;
                    grid-row: 1/2;
                    grid-column: 1/2;
                }
            }
        `,
    },
    descriptionColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${xScreen("lte", "md")} {
                grid-column: 3/-1;
                ${({ isSchedulable }: any) =>
                    isSchedulable
                        ? css`
                              grid-row: 2/3;
                          `
                        : css`
                              grid-row: 1/2;
                          `}
            }
        `,
    },
    totalSmallHeadingAndText: {
        ...createVerticalSmallHeadingAndTextStyles({
            wrapper: {
                css: css`
                    align-items: flex-start;
                    ${xScreen("lte", "md")} {
                        grid-column: 3 / 4;
                        grid-row: 3/4;
                    }
                `,
            },
            heading: {
                weight: fontConfig.weight.regular,
                ...fontConfig.singleLinexSmall,
                css: css`
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                `,
            },
            text: {
                weight: fontConfig.weight.medium,
                css: css`
                    text-align: center;
                    ${xScreen("lte", "md")} {
                        text-align: left;
                    }
                `,
            },
        }),
    },
    qtySmallHeadingAndText: {
        ...createVerticalSmallHeadingAndTextStyles({
            wrapper: {
                css: css`
                    text-align: center;
                    ${xScreen("lte", "md")} {
                        grid-column: 1 / 3;
                        grid-row: 3/4;
                        text-align: left;
                    }
                `,
            },
            heading: {
                weight: fontConfig.weight.regular,
                ...fontConfig.singleLinexSmall,
                css: css`
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                `,
            },
            text: {
                weight: fontConfig.weight.medium,
                css: css`
                    text-align: center;
                    ${xScreen("lte", "md")} {
                        text-align: left;
                    }
                `,
            },
        }),
    },
    qtyText: {
        weight: fontConfig.weight.regular,
        ...fontConfig.singleLineSmall,
        css: css`
            text-align: center;
            ${xScreen("lte", "md")} {
                text-align: left;
            }
        `,
    },
    asapText: {
        weight: fontConfig.weight.regular,
        ...fontConfig.singleLineSmall,
    },
    scheduleQtyColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;

            ${({ isSchedulable }: any) =>
                isSchedulable
                    ? css`
                          grid-column: 6 / -1;
                      `
                    : css`
                          grid-column: 5 / -1;
                      `}

            ${xScreen("lte", "md")} {
                grid-column: 1 / -1;
                grid-row: 4/5;
                align-items: flex-start;
                padding-top: 12px;
                border-top: 1px solid ${color("common.ui3")};
            }
        `,
    },
    productTitle: {
        weight: fontConfig.weight.medium,
        ...fontConfig.singleLineSmall,
        color: "text",
        css: css`
            display: block;
            letter-spacing: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        `,
    },
    partNumbers: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 5px;
        `,
    },
    partNumberRow: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    scheduleRow: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px;
            background: ${color("common.ui2")};
            border-radius: 4px;
        `,
    },
    scheduleRowAsap: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px;
            background: ${color("common.ui2")};
            border-radius: 4px;
        `,
    },
    scheduleRowAdded: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px;
            background: ${color("common.ui1")};
            border-radius: 4px;
            border: 1px solid ${color("common.ui3")};
        `,
    },
    addShipmentLink: {
        ...createLinkIconStyles({
            icon: PlusCircle,
            linkStyles: { typographyProps: { weight: fontConfig.weight.medium } },
        }),
    },
    qtyStepperWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 32px;
        `,
    },
    dateRowWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 8px;
            min-height: 32px;
        `,
    },
    deleteButton: {
        css: css`
            padding: 4px;
            cursor: pointer;
            color: ${color("text.accent")};
            display: flex;
            align-items: center;
            &:hover {
                color: ${color("danger.main")};
            }
        `,
    },
    datePicker: {
        cssOverrides: {
            datePicker: css`
                .react-datetime-picker {
                    height: 26px;
                    min-height: 26px;
                }
                .react-calendar {
                    left: -100px;
                    ${xScreen("lte", "lg")} {
                        left: -130px;
                    }
                    ${xScreen("lte", "md")} {
                        left: 0;
                    }
                }
            `,
        },
        calendarIconProps: {
            size: 20,
            css: css`
                &&& {
                    padding: 3px;
                }
            `,
        },
    },
    clickable: {
        css: css`
            &:hover,
            &:focus {
                ${IconWrapper} {
                    color: ${color("common.ui9")};
                }
            }
            margin-right: 16px;
            margin-left: 18px;
        `,
    },
};

const styles = scheduleOrderLineItemStyles;

const ScheduleOrderLineItem: FC<ScheduleOrderLineItemProps> = ({
    cart,
    cartLine,
    isSchedulable,
    isSelected,
    onCheckboxChange,
    scheduleState,
    minScheduleDate,
    onAddShipmentDate,
    onRemoveShipmentDate,
    onShipmentQtyChange,
    onShipmentDateChange,
    warningMessage,
}) => {
    const moqData = useMemo(() => {
        const minimumOrderQty = parseFloat(cartLine.properties?.minimumOrderQty || "1") || 1;
        const multipleSaleQty = parseFloat(cartLine.properties?.multipleSaleQty || "1") || 1;
        const isFractionalMoq = cartLine.properties?.isFractionalMoq?.toLowerCase() === "true";

        return {
            minimumOrderQty,
            multipleSaleQty,
            allowFractionalQty: isFractionalMoq || !Number.isInteger(minimumOrderQty),
        };
    }, [cartLine.properties]);

    const handleCheckboxChange = useCallback(
        (_: React.SyntheticEvent, isChecked: boolean) => {
            onCheckboxChange?.(cartLine.id, isChecked);
        },
        [cartLine.id, onCheckboxChange],
    );

    const handleAddShipmentClick = useCallback(() => {
        onAddShipmentDate?.(cartLine.id);
    }, [cartLine.id, onAddShipmentDate]);

    const handleShipmentQtyChange = useCallback(
        (shipmentId: string, qty: number) => {
            onShipmentQtyChange?.(cartLine.id, shipmentId, qty);
        },
        [cartLine.id, onShipmentQtyChange],
    );

    const handleShipmentDateChange = useCallback(
        (shipmentId: string, requestedDeliveryDate: string | null) => {
            onShipmentDateChange?.(cartLine.id, shipmentId, requestedDeliveryDate);
        },
        [cartLine.id, onShipmentDateChange],
    );

    const handleShipmentDelete = useCallback(
        (shipmentId: string) => {
            onRemoveShipmentDate?.(cartLine.id, shipmentId);
        },
        [cartLine.id, onRemoveShipmentDate],
    );

    const qtyOnBlurHandler = (e: React.FocusEvent<HTMLInputElement>, maxQtyForShipment: number, shipmentId: string) => {
        if (!e?.target?.value) {
            return;
        }
        let newQty = parseFloat(e.target.value) || 0;
        if (moqData.multipleSaleQty > 1 && newQty > 0) {
            newQty = Math.round(newQty / moqData.multipleSaleQty) * moqData.multipleSaleQty;
        }
        if (newQty > maxQtyForShipment) {
            newQty = maxQtyForShipment;
        }
        handleShipmentQtyChange(shipmentId, newQty);
    };

    const hasEisCertification = parseBoolean(cartLine.properties?.["eis/GCGCertification"]);
    const hasMfrCertification = parseBoolean(cartLine.properties?.mfrCertification);

    const items = scheduleState?.items || [
        { id: "default-asap", requestedDeliveryDate: null, qty: cartLine.qtyOrdered || 0 },
    ];

    const addedShipments = items.slice(1);

    const scheduledQty = addedShipments.reduce((sum, s) => sum + s.qty, 0);

    const asapQty = (cartLine.qtyOrdered || 0) - scheduledQty;

    const remainingQty = asapQty;

    const containerClass = isSchedulable ? "" : "no-checkbox";

    return (
        <StyledWrapper {...styles.wrapper}>
            {warningMessage && <Notify {...styles.warningBanner} messageType="warning" body={warningMessage} />}

            <StyledWrapper {...styles.container} className={containerClass}>
                {isSchedulable && (
                    <StyledWrapper {...styles.checkboxColumn}>
                        <Checkbox
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            data-test-selector={`scheduleOrder_lineCheckbox_${cartLine.id}`}
                        />
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.imageColumn}>
                    <ProductImage product={cartLine} extendedStyles={cartLineSharedStyles.productImage} />
                    <CertificationBadges
                        hasEisCertification={hasEisCertification}
                        hasMfrCertification={hasMfrCertification}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.descriptionColumn} {...{ isSchedulable }}>
                    <Typography {...styles.productTitle}>{cartLine.shortDescription}</Typography>
                    <StyledWrapper {...styles.partNumbers}>
                        {cartLine.manufacturerItem && (
                            <StyledWrapper {...styles.partNumberRow}>
                                <Typography {...xPartNumberStyle.label}>{translate("MFR #")}</Typography>
                                <Typography {...xPartNumberStyle.value}>{cartLine.manufacturerItem}</Typography>
                            </StyledWrapper>
                        )}
                        {cartLine.erpNumber && (
                            <StyledWrapper {...styles.partNumberRow}>
                                <Typography {...xPartNumberStyle.label}>{translate("EIS #")}</Typography>
                                <Typography {...xPartNumberStyle.value}>{cartLine.erpNumber}</Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>

                    {cartLine.brand && <ProductBrand showLabel={true} brand={cartLine.brand} />}
                </StyledWrapper>

                <SmallHeadingAndText
                    extendedStyles={styles.totalSmallHeadingAndText}
                    heading={translate("Total")}
                    text={cartLine.pricing?.extendedUnitNetPriceDisplay || "--"}
                />

                <SmallHeadingAndText
                    extendedStyles={styles.qtySmallHeadingAndText}
                    heading={translate("QTY In Cart")}
                    text={cartLine.qtyOrdered || ""}
                />

                <StyledWrapper {...styles.scheduleQtyColumn} {...{ isSchedulable }}>
                    <Hidden above="md">
                        <StyledWrapper className="hide-border" {...styles.qtyDateWrapper}>
                            <Typography {...styles.scheduleQtyAndDateLabel}>{translate("Schedule QTY")}</Typography>
                            <Typography {...styles.scheduleQtyAndDateLabel}>{translate("Schedule Date")}</Typography>
                        </StyledWrapper>
                    </Hidden>
                    <StyledWrapper className={!isSchedulable ? "hide-border" : ""} {...styles.qtyDateWrapper}>
                        <Typography {...styles.qtyText}>{Math.max(0, asapQty)}</Typography>
                        <Typography {...styles.asapText}>{translate("Ships ASAP")}</Typography>
                    </StyledWrapper>

                    {addedShipments.map(shipment => {
                        const maxQtyForShipment = remainingQty + shipment.qty;
                        const step = moqData.multipleSaleQty > 1 ? moqData.multipleSaleQty : 1;

                        return (
                            <StyledWrapper key={`qty-${shipment.id}`} {...styles.qtyDateWrapper}>
                                <StyledWrapper {...styles.qtyStepperWrapper}>
                                    <QtyField
                                        value={shipment.qty}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const newQty = parseFloat(e.target.value) || 0;
                                            handleShipmentQtyChange(shipment.id, newQty);
                                        }}
                                        onBlur={e => qtyOnBlurHandler(e, maxQtyForShipment, shipment.id)}
                                        onDecreaseChangeHandler={(e: any) => {
                                            const currentQty = shipment.qty;
                                            const newQty = Math.max(0, currentQty - step);
                                            handleShipmentQtyChange(shipment.id, newQty);
                                            e.target = { value: String(newQty) };
                                            return e;
                                        }}
                                        onIncreaseChangeHandler={(e: any) => {
                                            const currentQty = shipment.qty;
                                            const newQty = Math.min(maxQtyForShipment, currentQty + step);
                                            handleShipmentQtyChange(shipment.id, newQty);
                                            e.target = { value: String(newQty) };
                                            return e;
                                        }}
                                        minimumOrderQty={0}
                                        multipleSaleQty={moqData.multipleSaleQty}
                                        allowFractionalQty={moqData.allowFractionalQty}
                                        showLabel={false}
                                        allowZero={true}
                                        min={0}
                                        max={maxQtyForShipment}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.dateRowWrapper}>
                                    <DatePicker
                                        selectedDay={
                                            shipment.requestedDeliveryDate
                                                ? convertDateOnlyStringToDate(shipment.requestedDeliveryDate)
                                                : undefined
                                        }
                                        onDayChange={({ selectedDay }: DatePickerState) => {
                                            if (selectedDay) {
                                                handleShipmentDateChange(
                                                    shipment.id,
                                                    convertDateToDateOnlyString(selectedDay),
                                                );
                                            } else {
                                                handleShipmentDateChange(shipment.id, null);
                                            }
                                        }}
                                        dateTimePickerProps={{
                                            minDate: minScheduleDate,
                                        }}
                                        data-test-selector={`scheduleOrder_datePicker_${shipment.id}`}
                                        {...styles.datePicker}
                                    />
                                    <Clickable
                                        onClick={() => handleShipmentDelete(shipment.id)}
                                        css={styles.deleteButton?.css}
                                        aria-label={translate("Delete shipment")}
                                        data-test-selector={`scheduleOrder_deleteShipment_${shipment.id}`}
                                        {...styles.clickable}
                                    >
                                        <Icon color="primary" src={Delete} size={13} />
                                    </Clickable>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}

                    {isSchedulable && (
                        <Link onClick={handleAddShipmentClick} {...styles.addShipmentLink}>
                            {translate("Add Shipment Date")}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ScheduleOrderLineItem;
